import { Badge } from "@/components/ui/badge";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ApprenticeshipRegistrationLightType, ApprenticeshipRegistrationStatus, ApprenticeshipRegistrationType, CompanyTerminatedReason, StudentTerminatedReason } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { contratProMeetsSubmitRules } from "@/utils/contrat-eligibility";
import { CircleAlert, CircleArrowRight, CircleCheck, CircleCheckBig, CircleDashed, CircleX, ClipboardPenLine } from "lucide-react";
import { DateTime } from "luxon";

export const apprenticeship_application_status_badge = (application_status: ApprenticeshipRegistrationLightType["application_status"] | undefined, className?: string) => {
    switch (application_status) {
        case "interview_scheduled":
            return (
                <Badge variant="purple" className={className}>
                    Entretien programmé
                </Badge>
            );
        case "interview_passed":
            return (
                <Badge variant="default" className={className}>
                    Entretien passé
                </Badge>
            );
        case "interested":
            return (
                <Badge variant="amber" className={className}>
                    Intéressé
                </Badge>
            );
        case "resume_submitted":
            return (
                <Badge variant="blue" className={className}>
                    CV envoyé
                </Badge>
            );
        case "new":
            return (
                <Badge variant="green" className={className}>
                    Nouveau
                </Badge>
            );
        default:
            return (
                <Badge variant="green" className={className}>
                    Nouveau
                </Badge>
            );
    }
};

export const apprenticeship_status_badge = (registration: { status: ApprenticeshipRegistrationStatus }, className?: string) => {
    switch (registration.status) {
        case "Pending":
            return (
                <Badge variant="default" className={className}>
                    En attente
                </Badge>
            );
        case "Created":
            return (
                <Badge variant="default" className={className}>
                    Créé par un CS
                </Badge>
            );
        case "Fulfilled":
            return (
                <Badge variant="default" className={className}>
                    Dossier complété
                </Badge>
            );
        case "CompanyFulfilled":
            return (
                <Badge variant="teal" className={className}>
                    Dossier complété
                </Badge>
            );
        case "Submitted":
            return (
                <Badge variant="outline" className={className}>
                    Analyse en cours
                </Badge>
            );
        case "NoShow":
            return (
                <Badge variant="gray" className={className}>
                    Absent au jury
                </Badge>
            );
        case "SearchingCompany":
            return (
                <Badge variant="amber" className={className}>
                    Recherche entreprise
                </Badge>
            );
        case "InterviewUpcoming":
            return (
                <Badge variant="default" className={className}>
                    Entretien(s) à venir
                </Badge>
            );
        case "CompanyFound":
            return (
                <Badge variant="teal" className={className}>
                    Entreprise trouvée
                </Badge>
            );
        case "WaitingFeedback":
            return (
                <Badge variant="default" className={className}>
                    Entretien(s) à venir
                </Badge>
            );
        case "ContractToBeSigned":
            return (
                <Badge variant="teal" className={className}>
                    Contrat à signer
                </Badge>
            );
        case "ContractSigned":
            return (
                <Badge variant="teal" className={className}>
                    Signé
                </Badge>
            );
        case "Discarded":
            return (
                <Badge variant="destructive" className={className}>
                    Rejeté
                </Badge>
            );
        case "Abandonned":
            return (
                <Badge variant="gray" className={className}>
                    Abandonné
                </Badge>
            );
        case "Validated":
            return (
                <Badge variant="green" className={className}>
                    Validé
                </Badge>
            );
        case "Terminated":
            return (
                <Badge variant="red" className={className}>
                    Rupture
                </Badge>
            );
        case "Suspended":
            return (
                <Badge variant="red" className={className}>
                    Suspendu
                </Badge>
            );
        default:
            return (
                <Badge variant="default" className={className}>
                    Inconnu
                </Badge>
            );
    }
};

export const opco_status_badge = (opco_status: ApprenticeshipRegistrationType["opco_status"], className?: string) => {
    switch (opco_status) {
        case "Pending":
            return (
                <Badge variant="outline" className={className}>
                    En cours d'analyse
                </Badge>
            );
        case "Validated":
            return (
                <Badge variant="green" className={className}>
                    Validé
                </Badge>
            );
        case "Rejected":
            return (
                <Badge variant="destructive" className={className}>
                    Rejeté
                </Badge>
            );
        default:
            return <span className="text-gray-400">—</span>;
    }
};

export const apprenticeship_student_badge = (registration: ApprenticeshipRegistrationType, className?: string) => {
    switch (registration.student_state) {
        case "average":
            return (
                <Badge variant="amber" className={className}>
                    Moyen
                </Badge>
            );
        case "in_difficulty":
            return (
                <Badge variant="destructive" className={className}>
                    En difficulté
                </Badge>
            );
        case "performant":
            return (
                <Badge variant="green" className={className}>
                    Performant
                </Badge>
            );
        default:
            return (
                <Badge variant="default" className={className}>
                    Inconnu
                </Badge>
            );
    }
};
export const apprenticeship_company_badge = (registration: ApprenticeshipRegistrationType, className?: string) => {
    switch (registration.company_state) {
        case "average":
            return (
                <Badge variant="amber" className={className}>
                    Moyen
                </Badge>
            );
        case "in_difficulty":
            return (
                <Badge variant="destructive" className={className}>
                    En difficulté
                </Badge>
            );
        case "performant":
            return (
                <Badge variant="green" className={className}>
                    Performant
                </Badge>
            );
        default:
            return (
                <Badge variant="default" className={className}>
                    Inconnu
                </Badge>
            );
    }
};

export const appointment_status_badge = (appointment: ApprenticeshipAppointmentType, className?: string) => {
    const dt_start = DateTime.fromISO(appointment.dt_start);
    switch (appointment.status) {
        case "to_be_confirmed":
            return DateTime.now() >= dt_start.minus({ days: 1 }).startOf("day") ? (
                <Badge variant="amber" className={className}>
                    A confirmer
                </Badge>
            ) : (
                <Badge variant="outline" className={className}>
                    A venir
                </Badge>
            );
        case "confirmed":
            return (
                <Badge variant="default" className={className}>
                    Confirmé
                </Badge>
            );
        case "cancelled":
            return (
                <Badge variant="red" className={className}>
                    Annulé
                </Badge>
            );
        case "late_cancelled":
            return (
                <Badge variant="red" className={className}>
                    Annulé tardivement
                </Badge>
            );
        case "no_confirmation_cancelled":
            return (
                <Badge variant="red" className={className}>
                    Non confirmé
                </Badge>
            );
        case "finished":
            return (
                <Badge variant="green" className={className}>
                    Terminé
                </Badge>
            );
        case "absent":
            return (
                <Badge variant="gray" className={className}>
                    Absent
                </Badge>
            );
        default:
            return (
                <Badge variant="default" className={className}>
                    Inconnu
                </Badge>
            );
    }
};

export const appointment_status_icon = (appointment: ApprenticeshipAppointmentType, className?: string) => {
    const dt_start = DateTime.fromISO(appointment.dt_start);
    switch (appointment.status) {
        case "to_be_confirmed":
            return DateTime.now() >= dt_start.minus({ days: 1 }).startOf("day") ? (
                <SimpleTooltip message="Rendez-vous à confirmer">
                    <CircleAlert className={cn("text-amber-500", className)} />
                </SimpleTooltip>
            ) : (
                <SimpleTooltip message="Rendez-vous à venir">
                    <CircleArrowRight className={cn("text-blue-500", className)} />
                </SimpleTooltip>
            );
        case "confirmed":
            return (
                <SimpleTooltip message="Rendez-vous confirmé">
                    <CircleCheck className={cn("text-blue-500", className)} />
                </SimpleTooltip>
            );
        case "cancelled":
            return (
                <SimpleTooltip message="Rendez-vous annulé">
                    <CircleX className={cn("text-red-500", className)} />
                </SimpleTooltip>
            );
        case "late_cancelled":
            return (
                <SimpleTooltip message="Rendez-vous annulé tardivement">
                    <CircleX className={cn("text-red-500", className)} />
                </SimpleTooltip>
            );
        case "no_confirmation_cancelled":
            return (
                <SimpleTooltip message="Rendez-vous annulé car non confirmé">
                    <CircleAlert className={cn("text-red-500", className)} />
                </SimpleTooltip>
            );
        case "finished":
            return (
                <SimpleTooltip message="Rendez-vous terminé">
                    <CircleCheckBig className={cn("text-green-500", className)} />
                </SimpleTooltip>
            );
        case "absent":
            return (
                <SimpleTooltip message="Candidat absent">
                    <CircleAlert className={cn("text-red-500", className)} />
                </SimpleTooltip>
            );
        default:
            return (
                <SimpleTooltip message="Inconnu">
                    <CircleDashed className={cn("text-slate-500", className)} />
                </SimpleTooltip>
            );
    }
};

export const apprenticeship_termination_source = (registration: ApprenticeshipRegistrationType) => {
    switch (registration.terminated_source) {
        case "company":
            return "entreprise";
        case "student":
            return "étudiant";
        case "school":
            return "école";
        default:
            return "";
    }
};

export const apprenticeship_termination_reason = (registration: ApprenticeshipRegistrationType) => {
    if (registration.terminated_reason) {
        const TerminatedReason: { [key: string]: string } = {
            ...StudentTerminatedReason,
            ...CompanyTerminatedReason
        };
        const reason = TerminatedReason[registration.terminated_reason];
        return reason ? reason : 'non précisé';
    }
    return 'non précisé';
};

export const to_be_fullfilled = (value: any) => {
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger className="cursor-default">
                    <ClipboardPenLine className={cn("ml-1 h-4 w-4", value ? "opacity-0" : "text-orange-500")} />
                </TooltipTrigger>
                <TooltipContent>C'est à vous de remplir ce champ</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const eventTypes = [
    { id: 3, name: "Jury d'admission", code: "admission_jury", display: false, allowed_users: ["admin@walter-learning.com"] },
    { id: 5, name: "Point de suivi", code: "coaching_follow_up", display: true, allowed_users: ["admin@walter-learning.com"] },
    { id: 4, name: "Entretien blanc", code: "coaching_interview_preparation", display: true, allowed_users: ["admin@walter-learning.com"] },
    { id: 7, name: "Suivi de formation", code: "formation_follow_up", display: false, allowed_users: ["sofiane.khalfallaoui@walter-learning.com", "admin@walter-learning.com"] },
    { id: 8, name: "Entretien tri-partite", code: "tri_partite_interview", display: true, allowed_users: [] },
    { id: 16, name: "Onboarding élève", code: "onboarding", display: true, allowed_users: ["sofiane.khalfallaoui@walter-learning.com", "admin@walter-learning.com"] },
    { id: 17, name: "Onboarding entreprise", code: "onboarding_enterprise", display: true, allowed_users: ["sofiane.khalfallaoui@walter-learning.com", "admin@walter-learning.com"] },
    { id: 81, name: "Suivi Entreprise - Sofiane", code: "suivi_entreprise_sofiane", display: true, allowed_users: ["sofiane.khalfallaoui@walter-learning.com", "admin@walter-learning.com"] }
];

export const brevoTemplates = [
    // Jury d'admission
    // Deactivated as we transitioned to a unified template (627) that is setup on the EventType
    // {
    //     id: 312,
    //     product_code: "sec_med_alt",
    //     event_id: 3
    // },
    // {
    //     id: 351,
    //     product_code: "tp_advf_alt",
    //     event_id: 3
    // },
    // {
    //     id: 395,
    //     product_code: "cap_aepe_alt",
    //     event_id: 3
    // },
    // {
    //     id: 431,
    //     product_code: "tp_arh_alt",
    //     event_id: 3
    // },
    // {
    //     id: 468,
    //     product_code: "tp_gp_alt",
    //     event_id: 3
    // },
    // {
    //     id: 469,
    //     product_code: "tp_ntc_alt",
    //     event_id: 3
    // },
    // Entretien blanc
    {
        id: 304,
        product_code: "sec_med_alt",
        event_id: 4
    },
    {
        id: 304,
        product_code: "tp_sama_alt",
        event_id: 4
    },
    {
        id: 350,
        product_code: "tp_advf_alt",
        event_id: 4
    },
    {
        id: 396,
        product_code: "cap_aepe_alt",
        event_id: 4
    },
    {
        id: 432,
        product_code: "tp_arh_alt",
        event_id: 4
    },
    {
        id: 463,
        product_code: "tp_gp_alt",
        event_id: 4
    },
    {
        id: 462,
        product_code: "tp_ntc_alt",
        event_id: 4
    }
];

export const canBeSubmitted = (r: ApprenticeshipRegistrationType) => {
    if (r.apprenticeship_type == "ContratPro") {
        return contratProMeetsSubmitRules(r);
    } else if (r.apprenticeship_type == "Apprentissage") {
        return true;
    }
    return false;
};

export const state_badge = (state: string | null, className?: string) => {
    switch (state) {
        case "in_difficulty":
            return (
                <Badge variant="red" className={className}>
                    En difficulté
                </Badge>
            );
        case "average":
            return (
                <Badge variant="blue" className={className}>
                    Moyen
                </Badge>
            );
        case "performant":
            return (
                <Badge variant="green" className={className}>
                    Performant
                </Badge>
            );
        case "":
        case null:
            return "-";
        default:
            return (
                <Badge variant="default" className={className}>
                    {state}
                </Badge>
            );
    }
};

export const session_status_badge = (session: ApprenticeshipRegistrationType, className?: string) => {
    switch (session.session_status) {
        case "Validated":
            return (
                <Badge variant="green" className={className}>
                    Validé
                </Badge>
            );
        case "Ongoing":
            return (
                <Badge variant="amber" className={className}>
                    En cours
                </Badge>
            );

        case "Late":
            return (
                <Badge variant="red" className={className}>
                    En retard
                </Badge>
            );

        case "":
        case null:
            return "-";

        default:
            return (
                <Badge variant="default" className={className}>
                    {session.session_status}
                </Badge>
            );
    }
};

/** Seule formation qui expose des livrets de suivi tuteur (cf. `CAP_AEPE_PRODUCT_CODE` côté API). */
export const CAP_AEPE_PRODUCT_CODE = "cap_aepe_alt";

export const hasTutorMonitoringForms = (registration: ApprenticeshipRegistrationType) => registration.sf_product?.product_code === CAP_AEPE_PRODUCT_CODE;

/** Semestres attendus : 12 mois → 1,2 ; 18 mois et durée inconnue → 1,2,3 (même règle que le LMS tuteur). */
export const tutor_monitoring_semesters = (session_length: number | null | undefined): (1 | 2 | 3)[] => (session_length === 12 ? [1, 2] : [1, 2, 3]);
